/**
 * userProfileStore — Domain: Who is this person & what do they want
 *
 * Implicitly builds a user preference profile from search behavior across
 * all three surfaces (criteria search, vibe search, map browse).
 * Any surface can read the profile to provide continuity.
 *
 * Architecture:
 * - preferences: canonical set of what we know the user wants
 * - sources: which surface set each preference (for conflict resolution)
 * - history: recent searches for context
 * - Actions: updateFromCriteria (Criteria-input writes Dossier-worthy signals)
 * - Derived: getSearchContext() for injecting into LLM prompts
 *
 * Persisted to localStorage so preferences survive page refreshes.
 */
import { create } from 'zustand';
import { registerSearchReset } from './searchSession';
import { persist } from 'zustand/middleware';
import { safeLocal } from '@/utils/safeStorage';

type PreferenceSource = 'criteria_search' | 'vibe_search' | 'map_browse' | 'explicit' | 'apartment_note';

interface ApartmentNote {
  note: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  savedAt: string;
}

// Themes extracted from user notes
interface NoteInsight {
  theme: string;        // e.g., "pool", "noise", "commute", "modern"
  sentiment: 'positive' | 'negative';
  count: number;
  examples: string[];   // apartment names where this was noted
}

interface UserPreferences {
  bedrooms: string[];
  budgetMax: number | null;
  budgetMin: number | null;
  neighborhoods: string[];
  petFriendly: boolean | null;
  vibes: string[];           // "walkable", "nightlife", "quiet", etc.
  dealbreakers: string[];    // "no carpet", "no ground floor", etc.
  amenities: string[];       // "pool", "gym", "in-unit laundry"
  buildingAges: string[];
  moveInDate: string | null;
  workLocation: string | null;
  maxCommute: number | null;
  minSqft: number | null;
  city: string;
}

interface PreferenceSources {
  bedrooms?: PreferenceSource;
  budgetMax?: PreferenceSource;
  budgetMin?: PreferenceSource;
  neighborhoods?: PreferenceSource;
  petFriendly?: PreferenceSource;
  vibes?: PreferenceSource;
  dealbreakers?: PreferenceSource;
  amenities?: PreferenceSource;
  buildingAges?: PreferenceSource;
  moveInDate?: PreferenceSource;
  workLocation?: PreferenceSource;
  city?: PreferenceSource;
}

interface RecentSearch {
  query: string;
  source: 'criteria' | 'vibe' | 'map';
  timestamp: string;
  city: string;
}

interface UserProfileStoreState {
  preferences: UserPreferences;
  sources: PreferenceSources;
  recentSearches: RecentSearch[];
  apartmentNotes: Record<string, ApartmentNote>;
  noteInsights: NoteInsight[];
  savedApartmentNames: string[];
  lastUpdated: string | null;

  // ── Write actions (each surface writes here) ───────────────────
  updateFromCriteria: (filters: Record<string, unknown>) => void;
  updateExplicit: (prefs: Partial<UserPreferences>) => void;
  addRecentSearch: (search: Omit<RecentSearch, 'timestamp'>) => void;

  // ── Apartment notes ────────────────────────────────────────────
  setApartmentNote: (aptName: string, note: string) => void;
  removeApartmentNote: (aptName: string) => void;
  getApartmentNote: (aptName: string) => string;

  // ── Saves + notes sync (Phase 1 — Context System) ──────────────
  syncFromLocalStorage: () => void;
  syncFromAccount: (
    saves: Array<{ name?: string; apartment_data?: { name?: string } }>,
    notes: Record<string, string>,
  ) => void;

  // ── Read helpers ───────────────────────────────────────────────
  /** Build a plain-English context string for LLM injection */
  getSearchContext: () => string;
  /** Build a structured object for API calls */
  getProfileForApi: () => Record<string, unknown>;
  /** Check if profile has meaningful data */
  hasPreferences: () => boolean;

  // ── Management ─────────────────────────────────────────────────
  clearPreferences: () => void;
  removePreference: (key: keyof UserPreferences) => void;
  /** Brief 1 §3.1 — Reset every search-context preference (bedrooms, budget,
   *  neighborhoods, vibes, dealbreakers, amenities, work, sqft, move-in)
   *  but PRESERVE account-level data (city, name/email handled elsewhere)
   *  and apartment notes / saved names. Called on every "+ New Search". */
  resetSearchContext: () => void;
}

const EMPTY_PREFS: UserPreferences = {
  bedrooms: [],
  budgetMax: null,
  budgetMin: null,
  neighborhoods: [],
  petFriendly: null,
  vibes: [],
  dealbreakers: [],
  amenities: [],
  buildingAges: [],
  moveInDate: null,
  workLocation: null,
  maxCommute: null,
  minSqft: null,
  city: 'Austin',
};

/**
 * Merge new values into preferences.
 * Rule: newer data wins, but don't overwrite with empty/null unless explicit.
 */

// ── Note Insight Extraction ─────────────────────────────────────
const NOTE_THEME_PATTERNS: { theme: string; positive: RegExp; negative: RegExp }[] = [
  { theme: 'pool', positive: /\b(love|great|amazing|nice|beautiful)\b.*\bpool/i, negative: /\b(no pool|pool.*small|pool.*dirty|pool.*crowded)/i },
  { theme: 'noise', positive: /\b(quiet|peaceful|silent|serene)/i, negative: /\b(noise|noisy|loud|traffic|busy street|highway)/i },
  { theme: 'commute', positive: /\b(close to work|short commute|easy drive|near office)/i, negative: /\b(too far|long commute|far from work|traffic)/i },
  { theme: 'modern', positive: /\b(modern|new|renovated|updated|fresh|sleek)/i, negative: /\b(dated|old|outdated|worn|tired|needs work)/i },
  { theme: 'gym', positive: /\b(great gym|nice gym|love.*gym|good fitness)/i, negative: /\b(no gym|gym.*small|gym.*bad)/i },
  { theme: 'dog park', positive: /\b(dog park|great.*dog|love.*dog|dog friendly)/i, negative: /\b(no dog|dog.*small|not.*dog)/i },
  { theme: 'management', positive: /\b(great management|friendly staff|responsive|helpful)/i, negative: /\b(bad management|rude|unresponsive|terrible staff|management.*issue)/i },
  { theme: 'price', positive: /\b(good value|affordable|great price|worth it|deal)/i, negative: /\b(overpriced|expensive|pricey|too much|not worth)/i },
  { theme: 'layout', positive: /\b(great layout|spacious|open.*floor|big rooms)/i, negative: /\b(small|cramped|tiny|bad layout|awkward)/i },
  { theme: 'parking', positive: /\b(great parking|easy parking|garage|covered parking)/i, negative: /\b(no parking|parking.*bad|parking.*expensive|hard to park)/i },
  { theme: 'walkability', positive: /\b(walkable|walk.*everywhere|great location|close to)/i, negative: /\b(not walkable|car.*dependent|nothing nearby|isolated)/i },
  { theme: 'view', positive: /\b(great view|skyline|beautiful view|overlook)/i, negative: /\b(no view|parking lot view|ugly view)/i },
];

function extractNoteInsights(notes: Record<string, ApartmentNote>): NoteInsight[] {
  const insightMap: Record<string, NoteInsight> = {};

  for (const [aptName, noteObj] of Object.entries(notes)) {
    const text = noteObj.note;
    for (const pattern of NOTE_THEME_PATTERNS) {
      if (pattern.positive.test(text)) {
        const key = `${pattern.theme}_positive`;
        if (!insightMap[key]) insightMap[key] = { theme: pattern.theme, sentiment: 'positive', count: 0, examples: [] };
        insightMap[key].count++;
        if (insightMap[key].examples.length < 3) insightMap[key].examples.push(aptName);
      }
      if (pattern.negative.test(text)) {
        const key = `${pattern.theme}_negative`;
        if (!insightMap[key]) insightMap[key] = { theme: pattern.theme, sentiment: 'negative', count: 0, examples: [] };
        insightMap[key].count++;
        if (insightMap[key].examples.length < 3) insightMap[key].examples.push(aptName);
      }
    }
  }

  return Object.values(insightMap).sort((a, b) => b.count - a.count);
}

function detectSentiment(text: string): 'positive' | 'negative' | 'neutral' {
  const positiveWords = /\b(love|great|amazing|beautiful|perfect|nice|good|excellent|awesome|fantastic|best|favorite|wonderful)\b/i;
  const negativeWords = /\b(hate|terrible|bad|awful|worst|horrible|ugly|dirty|old|dated|noisy|expensive|rude|small|cramped|far)\b/i;
  const posCount = (text.match(positiveWords) || []).length;
  const negCount = (text.match(negativeWords) || []).length;
  if (posCount > negCount) return 'positive';
  if (negCount > posCount) return 'negative';
  return 'neutral';
}

function mergePrefs(
  current: UserPreferences,
  incoming: Partial<UserPreferences>,
  source: PreferenceSource,
  currentSources: PreferenceSources,
): { prefs: UserPreferences; sources: PreferenceSources } {
  const prefs = { ...current };
  const sources = { ...currentSources };

  for (const [key, value] of Object.entries(incoming)) {
    const k = key as keyof UserPreferences;

    // Skip null/undefined/empty unless it's an explicit clear
    if (source !== 'explicit' && (value === null || value === undefined)) continue;
    if (source !== 'explicit' && Array.isArray(value) && value.length === 0) continue;

    // Explicit source always wins. Otherwise, any non-empty value updates.
    const currentSource = sources[k as keyof PreferenceSources];
    if (currentSource === 'explicit' && source !== 'explicit') continue;

    (prefs as Record<string, unknown>)[k] = value;
    (sources as Record<string, PreferenceSource | undefined>)[k as keyof PreferenceSources] = source;
  }

  return { prefs, sources };
}

export const useUserProfileStore = create<UserProfileStoreState>()(
  persist(
    (set, get) => ({
      preferences: { ...EMPTY_PREFS },
      sources: {},
      recentSearches: [],
      apartmentNotes: {},
      noteInsights: [],
      savedApartmentNames: [],
      lastUpdated: null,

      // ── From criteria search filters ─────────────────────────────
      // PR C2: only Dossier-worthy signals are auto-promoted. Transient
      // Search criteria (bedrooms, budget, neighborhoods, vibes, amenities,
      // building_ages, min_sqft, move-in date, pet_friendly) live in
      // apartmentStore.filters (the canonical filter panel per ADR-0001)
      // and are sent to the backend as `current_filters` on each turn.
      updateFromCriteria: (filters) => {
        const incoming: Partial<UserPreferences> = {};

        if (filters.city) incoming.city = filters.city as string;
        if (filters.workLocationText) incoming.workLocation = filters.workLocationText as string;
        if (filters.maxCommute) incoming.maxCommute = filters.maxCommute as number;

        // Dealbreakers from boolean flags — explicit "I won't" signals
        const dbs: string[] = [];
        if (filters.noGroundFloor) dbs.push('no ground floor');
        if (filters.noStreetFacing) dbs.push('no street facing');
        if (filters.noCarpet) dbs.push('no carpet');
        if (filters.mustHaveCoveredParking) dbs.push('must have covered parking');
        if (filters.mustAllowLargeDogs) dbs.push('must allow large dogs');
        if (dbs.length > 0) incoming.dealbreakers = dbs;

        if (Object.keys(incoming).length === 0) return;

        const { prefs, sources } = mergePrefs(
          get().preferences, incoming, 'criteria_search', get().sources
        );
        set({ preferences: prefs, sources, lastUpdated: new Date().toISOString() });
      },

      // ── Explicit user override ───────────────────────────────────
      updateExplicit: (prefs) => {
        const { prefs: merged, sources } = mergePrefs(
          get().preferences, prefs, 'explicit', get().sources
        );
        set({ preferences: merged, sources, lastUpdated: new Date().toISOString() });
      },

      addRecentSearch: (search) => {
        set(s => ({
          recentSearches: [
            { ...search, timestamp: new Date().toISOString() },
            ...s.recentSearches.slice(0, 19), // Keep last 20
          ],
        }));
      },

      // ── Apartment Notes ───────────────────────────────────────────
      setApartmentNote: (aptName, note) => {
        const trimmed = note.trim();
        if (!trimmed) {
          // Remove note if empty
          get().removeApartmentNote(aptName);
          return;
        }
        set(s => {
          const newNotes = {
            ...s.apartmentNotes,
            [aptName]: {
              note: trimmed,
              sentiment: detectSentiment(trimmed),
              savedAt: new Date().toISOString(),
            },
          };
          return {
            apartmentNotes: newNotes,
            noteInsights: extractNoteInsights(newNotes),
            lastUpdated: new Date().toISOString(),
          };
        });
      },

      removeApartmentNote: (aptName) => {
        set(s => {
          const newNotes = { ...s.apartmentNotes };
          delete newNotes[aptName];
          return {
            apartmentNotes: newNotes,
            noteInsights: extractNoteInsights(newNotes),
            lastUpdated: new Date().toISOString(),
          };
        });
      },

      getApartmentNote: (aptName) => {
        return get().apartmentNotes[aptName]?.note || '';
      },

      // ── Saves + notes sync (Phase 1 — Context System) ─────────────
      syncFromLocalStorage: () => {
        // Read saved apartment names
        let savedNames: string[] = [];
        try {
          const raw = safeLocal.get('tacostreet_local_saves');
          const saves = JSON.parse(raw || '[]') as Array<{ name?: string }>;
          savedNames = saves.map(a => a.name).filter((n): n is string => !!n);
        } catch (e) { console.warn('local saves read failed', e); }

        // Read notes from SavedListPage's key (NOTES_KEY = 'tacostreet_local_notes')
        let localNotes: Record<string, string> = {};
        try {
          const raw = safeLocal.get('tacostreet_local_notes');
          localNotes = JSON.parse(raw || '{}');
        } catch (e) { console.warn('local notes read failed', e); }

        // Merge local notes into apartmentNotes
        const currentNotes = get().apartmentNotes;
        const mergedNotes = { ...currentNotes };

        for (const [aptName, noteText] of Object.entries(localNotes)) {
          const trimmed = (noteText || '').trim();
          if (!trimmed) continue;
          if (!currentNotes[aptName] || currentNotes[aptName].note !== trimmed) {
            mergedNotes[aptName] = {
              note: trimmed,
              sentiment: detectSentiment(trimmed),
              savedAt: currentNotes[aptName]?.savedAt || new Date().toISOString(),
            };
          }
        }

        set({
          savedApartmentNames: savedNames,
          apartmentNotes: mergedNotes,
          noteInsights: extractNoteInsights(mergedNotes),
          lastUpdated: new Date().toISOString(),
        });
      },

      syncFromAccount: (saves, notes) => {
        const savedNames = saves
          .map(s => s?.name || s?.apartment_data?.name)
          .filter((n): n is string => !!n);

        const currentNotes = get().apartmentNotes;
        const mergedNotes = { ...currentNotes };

        for (const [aptName, noteText] of Object.entries(notes)) {
          const trimmed = (noteText || '').trim();
          if (!trimmed) continue;
          mergedNotes[aptName] = {
            note: trimmed,
            sentiment: detectSentiment(trimmed),
            savedAt: currentNotes[aptName]?.savedAt || new Date().toISOString(),
          };
        }

        set({
          savedApartmentNames: savedNames,
          apartmentNotes: mergedNotes,
          noteInsights: extractNoteInsights(mergedNotes),
          lastUpdated: new Date().toISOString(),
        });
      },

      // ── Read helpers ─────────────────────────────────────────────
      getSearchContext: () => {
        const p = get().preferences;
        const insights = get().noteInsights;
        const parts: string[] = [];

        if (p.bedrooms.length > 0) parts.push(`looking for ${p.bedrooms.join(' or ')} bedrooms`);
        if (p.budgetMax) parts.push(`budget up to $${p.budgetMax.toLocaleString()}`);
        if (p.budgetMin && p.budgetMax) parts.push(`budget range $${p.budgetMin.toLocaleString()}-$${p.budgetMax.toLocaleString()}`);
        else if (p.budgetMin) parts.push(`minimum budget $${p.budgetMin.toLocaleString()}`);
        if (p.neighborhoods.length > 0) parts.push(`prefers ${p.neighborhoods.join(', ')}`);
        if (p.petFriendly) parts.push('needs pet-friendly');
        if (p.vibes.length > 0) parts.push(`vibes: ${p.vibes.join(', ')}`);
        if (p.amenities.length > 0) parts.push(`wants: ${p.amenities.join(', ')}`);
        if (p.dealbreakers.length > 0) parts.push(`dealbreakers: ${p.dealbreakers.join(', ')}`);
        if (p.workLocation) parts.push(`works at/near ${p.workLocation}`);
        if (p.maxCommute) parts.push(`max ${p.maxCommute} min commute`);
        if (p.minSqft) parts.push(`min ${p.minSqft} sqft`);
        if (p.moveInDate) parts.push(`moving ${p.moveInDate}`);

        // Add note-derived insights
        if (insights.length > 0) {
          const positives = insights.filter(i => i.sentiment === 'positive').map(i => i.theme);
          const negatives = insights.filter(i => i.sentiment === 'negative').map(i => i.theme);
          if (positives.length > 0) parts.push(`values (from notes): ${positives.join(', ')}`);
          if (negatives.length > 0) parts.push(`concerns (from notes): ${negatives.join(', ')}`);
        }

        // Add saved apartments — highest-signal context
        const savedNames = get().savedApartmentNames;
        if (savedNames.length > 0) {
          const savedWithNotes = savedNames.slice(0, 10).map(name => {
            const note = get().apartmentNotes[name]?.note;
            return note ? `${name} ("${note}")` : name;
          }).join(', ');
          parts.push(`already saved: ${savedWithNotes}`);
        }

        if (parts.length === 0) return '';
        return `User profile: ${parts.join('; ')}.`;
      },

      getProfileForApi: () => {
        const p = get().preferences;
        const insights = get().noteInsights;
        const notes = get().apartmentNotes;
        const profile: Record<string, unknown> = {};

        // PR C2: Dossier emits only revealed-preference signal — saves,
        // notes, insights — plus persistent dealbreakers and work location.
        // Transient Search criteria (bedrooms, budget, neighborhoods, vibes,
        // amenities, building_ages, min_sqft, move-in, pet_friendly) are sent
        // as a separate `current_filters` field by vibeStore.sendMessage.
        if (p.dealbreakers.length > 0) profile.dealbreakers = p.dealbreakers;
        if (p.workLocation) profile.work_location = p.workLocation;
        if (p.maxCommute) profile.max_commute = p.maxCommute;

        // Include note insights for AI
        if (insights.length > 0) {
          profile.note_insights = insights.map(i => ({
            theme: i.theme, sentiment: i.sentiment, count: i.count,
          }));
        }

        // Include raw notes on specific apartments (for cross-referencing)
        const noteEntries = Object.entries(notes);
        if (noteEntries.length > 0) {
          profile.apartment_notes = Object.fromEntries(
            noteEntries.slice(0, 20).map(([name, n]) => [name, { note: n.note, sentiment: n.sentiment }])
          );
        }

        // Saved apartments with their notes — highest-value context for the AI
        const savedNames = get().savedApartmentNames;
        if (savedNames.length > 0) {
          profile.saved_apartments = savedNames.slice(0, 30).map(name => ({
            name,
            note: notes[name]?.note || null,
            sentiment: notes[name]?.sentiment || null,
          }));
        }

        return profile;
      },

      hasPreferences: () => {
        const p = get().preferences;
        const notes = get().apartmentNotes;
        const saves = get().savedApartmentNames;
        // PR C2: only Dossier-worthy signals count. Transient Search criteria
        // (bedrooms, budget, neighborhoods, vibes, amenities, etc.) are no
        // longer in the Dossier — they live in apartmentStore.filters.
        return !!(
          p.dealbreakers.length > 0 || p.workLocation || p.maxCommute ||
          Object.keys(notes).length > 0 || saves.length > 0
        );
      },

      clearPreferences: () => {
        set({
          preferences: { ...EMPTY_PREFS },
          sources: {},
          lastUpdated: null,
        });
      },

      removePreference: (key) => {
        set(s => {
          const prefs = { ...s.preferences };
          const sources = { ...s.sources };

          // Reset to default
          if (Array.isArray(EMPTY_PREFS[key])) {
            (prefs as Record<string, unknown>)[key] = [];
          } else {
            (prefs as Record<string, unknown>)[key] = null;
          }
          delete sources[key as keyof PreferenceSources];

          return { preferences: prefs, sources, lastUpdated: new Date().toISOString() };
        });
      },

      // Clears transient Search criteria from the Dossier on "+ New Search".
      // Preserves persistent Dossier signals (dealbreakers, work_location,
      // max_commute, city, notes, saves) — those survive across Searches.
      //
      // PR C2: after this PR transient fields are no longer written here, so
      // this method is mostly defensive against legacy persisted state. The
      // canonical "reset Search criteria on new Session" now lives in
      // apartmentStore.resetFilters (called from vibeStore.createSession).
      resetSearchContext: () => {
        set(s => ({
          preferences: {
            ...EMPTY_PREFS,
            city: s.preferences.city,
            dealbreakers: s.preferences.dealbreakers,
            workLocation: s.preferences.workLocation,
            maxCommute: s.preferences.maxCommute,
          },
          sources: {},
          lastUpdated: new Date().toISOString(),
        }));
      },
    }),
    {
      name: 'taco-user-profile',
      partialize: (state) => ({
        preferences: state.preferences,
        sources: state.sources,
        recentSearches: state.recentSearches.slice(0, 10),
        apartmentNotes: state.apartmentNotes,
        noteInsights: state.noteInsights,
        savedApartmentNames: state.savedApartmentNames,
        lastUpdated: state.lastUpdated,
      }),
    }
  )
);

// searchSession registry — clear legacy persisted transient criteria
// (ADR-0007: Dossier signals survive; transient Search criteria don't).
registerSearchReset('userProfileStore', () => useUserProfileStore.getState().resetSearchContext());
